/**
 * PaymentTabs Component
 * Reusable payment tabs matching PaymentMatching page structure:
 * - Overdue Payments
 * - Upcoming Payments
 * - Matched Payments
 * - Pending Capital Receipts (optional)
 * - Matched Capital Receipts
 *
 * Used in both Contract Wizard (Step 5) and PaymentMatching page
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { format } from 'date-fns';
import TabPanel from '../common/TabPanel';
import OverduePaymentsTab from './OverduePaymentsTab';
import UpcomingPaymentsTab from './UpcomingPaymentsTab';
import { isPaymentNarration, differenceInDays } from '../../utils/paymentHelpers';
import ContractReferenceCell from '../contracts/ContractReferenceCell';
import { formatNumber } from '../../utils/validation';

function PaymentTabs({
  contractId,
  investorId,
  // Schedule-based filtering (for contract wizard)
  schedule = [],
  // Pre-filtered data (for PaymentMatching page)
  overdueData,      // If provided, use this instead of filtering schedule
  upcomingData,     // If provided, use this instead of filtering schedule
  // Completed payments data
  payments = [],
  receipts = [],
  pendingReceipts = [], // Pending capital draw downs (optional)
  currencyCode = 'ZAR',
  onMatch,
  onRefresh,
  onReceipt,        // Callback for receipting pending capital
  showMatchActions = true,
  showContractColumn = true,
  showInvestorColumn = false,
  defaultTab = 'auto',
  // Filter visibility (hide filter in PaymentMatching since API handles it)
  showUpcomingFilter = true,
  // Show pending receipts tab (only in PaymentMatching, not in contract wizard)
  showPendingReceiptsTab = false,
}) {
  const [tabValue, setTabValue] = useState(0);
  const [upcomingFilter, setUpcomingFilter] = useState('30days');
  const [customDate, setCustomDate] = useState(null);

  // Determine if we're using pre-filtered data or schedule-based filtering
  const usePreFilteredData = overdueData !== undefined || upcomingData !== undefined;

  // Memoized data filtering (only when using schedule-based filtering)
  const { overduePayments, upcomingPayments, allUpcomingPayments } = useMemo(() => {
    // If pre-filtered data is provided, use it directly
    if (usePreFilteredData) {
      return {
        overduePayments: overdueData || [],
        upcomingPayments: upcomingData || [],
        allUpcomingPayments: upcomingData || [],
      };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Filter to payment narrations only (Interest Payment, Capital Payment, Adhoc Capital Withdrawal)
    const paymentEntries = (schedule || []).filter(entry => isPaymentNarration(entry.narration));

    // Overdue: payment_date < today AND status is Pending
    const overdue = paymentEntries
      .filter(entry => {
        const paymentDate = new Date(entry.payment_date);
        paymentDate.setHours(0, 0, 0, 0);
        return entry.status === 'Pending' && paymentDate < today;
      })
      .map(entry => ({
        ...entry,
        days_overdue: differenceInDays(today, new Date(entry.payment_date))
      }))
      .sort((a, b) => b.days_overdue - a.days_overdue); // Most overdue first

    // All upcoming: payment_date >= today AND status is Pending
    const allUpcoming = paymentEntries
      .filter(entry => {
        const paymentDate = new Date(entry.payment_date);
        paymentDate.setHours(0, 0, 0, 0);
        return entry.status === 'Pending' && paymentDate >= today;
      })
      .sort((a, b) => new Date(a.payment_date) - new Date(b.payment_date));

    // Apply date filter to upcoming
    let filteredUpcoming = [...allUpcoming];
    if (upcomingFilter === '30days') {
      filteredUpcoming = allUpcoming.filter(e => differenceInDays(new Date(e.payment_date), today) <= 30);
    } else if (upcomingFilter === '90days') {
      filteredUpcoming = allUpcoming.filter(e => differenceInDays(new Date(e.payment_date), today) <= 90);
    } else if (upcomingFilter === 'custom' && customDate) {
      filteredUpcoming = allUpcoming.filter(e => new Date(e.payment_date) <= customDate);
    }

    return {
      overduePayments: overdue,
      upcomingPayments: filteredUpcoming,
      allUpcomingPayments: allUpcoming,
    };
  }, [schedule, upcomingFilter, customDate, usePreFilteredData, overdueData, upcomingData]);

  // Calculate tab indices based on whether pending receipts tab is shown
  const tabIndices = useMemo(() => {
    if (showPendingReceiptsTab) {
      return {
        overdue: 0,
        upcoming: 1,
        matched: 2,
        pendingReceipts: 3,
        matchedReceipts: 4,
      };
    }
    return {
      overdue: 0,
      upcoming: 1,
      matched: 2,
      matchedReceipts: 3,
    };
  }, [showPendingReceiptsTab]);

  // Auto-select default tab based on data
  useEffect(() => {
    if (defaultTab === 'auto') {
      if (overduePayments.length > 0) {
        setTabValue(tabIndices.overdue);
      } else if (allUpcomingPayments.length > 0) {
        setTabValue(tabIndices.upcoming);
      } else if (showPendingReceiptsTab && pendingReceipts.length > 0) {
        setTabValue(tabIndices.pendingReceipts);
      } else {
        setTabValue(tabIndices.matched);
      }
    } else if (defaultTab === 'overdue') {
      setTabValue(tabIndices.overdue);
    } else if (defaultTab === 'upcoming') {
      setTabValue(tabIndices.upcoming);
    } else if (defaultTab === 'matched') {
      setTabValue(tabIndices.matched);
    }
  }, [defaultTab, overduePayments.length, allUpcomingPayments.length, pendingReceipts.length, tabIndices, showPendingReceiptsTab]);

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      {/* Tab header with badges */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            label={
              overduePayments.length > 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Overdue Payments</span>
                  <Chip label={overduePayments.length} size="small" color="error" sx={{ height: 20, fontSize: '0.75rem' }} />
                </Box>
              ) : 'Overdue Payments'
            }
          />
          <Tab
            label={
              allUpcomingPayments.length > 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Upcoming Payments</span>
                  <Chip label={allUpcomingPayments.length} size="small" color="primary" sx={{ height: 20, fontSize: '0.75rem' }} />
                </Box>
              ) : 'Upcoming Payments'
            }
          />
          <Tab label="Matched Payments" />
          {showPendingReceiptsTab && (
            <Tab
              label={
                pendingReceipts.length > 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>Pending Capital Receipts</span>
                    <Chip label={pendingReceipts.length} size="small" color="warning" sx={{ height: 20, fontSize: '0.75rem' }} />
                  </Box>
                ) : 'Pending Capital Receipts'
              }
            />
          )}
          <Tab label="Matched Capital Receipts" />
        </Tabs>

        {onRefresh && (
          <Tooltip title="Refresh">
            <IconButton onClick={onRefresh} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Overdue Payments Tab */}
      <TabPanel value={tabValue} index={tabIndices.overdue} id="payments" padding={0}>
        <OverduePaymentsTab
          payments={overduePayments}
          currencyCode={currencyCode}
          showContractColumn={showContractColumn}
          showInvestorColumn={showInvestorColumn}
          showMatchActions={showMatchActions}
          onMatch={onMatch}
        />
      </TabPanel>

      {/* Upcoming Payments Tab */}
      <TabPanel value={tabValue} index={tabIndices.upcoming} id="payments" padding={0}>
        <UpcomingPaymentsTab
          payments={upcomingPayments}
          currencyCode={currencyCode}
          showContractColumn={showContractColumn}
          showInvestorColumn={showInvestorColumn}
          showMatchActions={showMatchActions}
          onMatch={onMatch}
          filter={upcomingFilter}
          onFilterChange={setUpcomingFilter}
          customDate={customDate}
          onCustomDateChange={setCustomDate}
          showFilter={showUpcomingFilter}
        />
      </TabPanel>

      {/* Matched Payments Tab */}
      <TabPanel value={tabValue} index={tabIndices.matched} id="payments" padding={0}>
        <MatchedPaymentsTab
          payments={payments}
          currencyCode={currencyCode}
          showContractColumn={showContractColumn}
          showInvestorColumn={showInvestorColumn}
        />
      </TabPanel>

      {/* Pending Capital Receipts Tab (optional) */}
      {showPendingReceiptsTab && (
        <TabPanel value={tabValue} index={tabIndices.pendingReceipts} id="payments" padding={0}>
          <PendingReceiptsTab
            receipts={pendingReceipts}
            currencyCode={currencyCode}
            showContractColumn={showContractColumn}
            showInvestorColumn={showInvestorColumn}
            onReceipt={onReceipt}
          />
        </TabPanel>
      )}

      {/* Matched Capital Receipts Tab */}
      <TabPanel value={tabValue} index={tabIndices.matchedReceipts} id="payments" padding={0}>
        <MatchedReceiptsTab
          receipts={receipts}
          currencyCode={currencyCode}
          showContractColumn={showContractColumn}
          showInvestorColumn={showInvestorColumn}
        />
      </TabPanel>
    </Box>
  );
}

// Matched Payments Tab Component
function MatchedPaymentsTab({
  payments = [],
  currencyCode = 'ZAR',
  showContractColumn = true,
  showInvestorColumn = false,
}) {
  if (payments.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography color="textSecondary" fontWeight="medium" gutterBottom>
          No matched payments yet
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Matched payments will appear here once recorded
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
            {showContractColumn && <TableCell>Contract</TableCell>}
            {showInvestorColumn && <TableCell>Investor</TableCell>}
            <TableCell>Payment Date</TableCell>
            <TableCell>Type</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell>Reference</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {payments.map((payment, index) => (
            <TableRow key={payment.id || `payment-${index}`} hover>
              {showContractColumn && (
                <TableCell>
                  <ContractReferenceCell reference={payment.contract_reference || '-'} userReference={payment.user_reference} />
                </TableCell>
              )}
              {showInvestorColumn && (
                <TableCell>
                  <Typography variant="body2">
                    {payment.investor_name || '-'}
                  </Typography>
                </TableCell>
              )}
              <TableCell>
                {payment.payment_date
                  ? format(new Date(payment.payment_date), 'dd MMM yyyy')
                  : '-'}
              </TableCell>
              <TableCell>
                <Chip
                  label={payment.payment_type || payment.narration || 'Payment'}
                  size="small"
                  color={
                    (payment.payment_type || payment.narration)?.includes('Interest') ? 'info' :
                    (payment.payment_type || payment.narration)?.includes('Capital') ? 'success' :
                    (payment.payment_type || payment.narration)?.includes('Full Settlement') ? 'warning' : 'default'
                  }
                />
              </TableCell>
              <TableCell align="right">
                <Typography fontWeight="medium">
                  {payment.currency_code || currencyCode} {formatNumber(payment.payment_amount)}
                </Typography>
              </TableCell>
              <TableCell>{payment.payment_reference || '-'}</TableCell>
              <TableCell>
                <Chip
                  icon={<CheckCircleIcon />}
                  label={payment.payment_status || 'Matched'}
                  size="small"
                  color="success"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Pending Receipts Tab Component
function PendingReceiptsTab({
  receipts = [],
  currencyCode = 'ZAR',
  showContractColumn = true,
  showInvestorColumn = false,
  onReceipt,
}) {
  if (receipts.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
        <Typography color="success.main" fontWeight="medium" gutterBottom>
          All capital has been received
        </Typography>
        <Typography variant="body2" color="textSecondary">
          No pending capital draw downs for active contracts
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#fff3e0' }}>
            {showContractColumn && <TableCell>Contract</TableCell>}
            {showInvestorColumn && <TableCell>Investor</TableCell>}
            <TableCell>Expected Date</TableCell>
            <TableCell align="right">Capital Amount</TableCell>
            <TableCell>Status</TableCell>
            {onReceipt && <TableCell align="center">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {receipts.map((receipt, index) => (
            <TableRow
              key={receipt.id || `pending-${index}`}
              hover
              sx={{
                bgcolor: receipt.days_overdue > 0 ? 'error.50' : 'inherit',
                '&:hover': { bgcolor: receipt.days_overdue > 0 ? 'error.100' : undefined },
              }}
            >
              {showContractColumn && (
                <TableCell>
                  <ContractReferenceCell reference={receipt.contract_reference || '-'} userReference={receipt.user_reference} />
                </TableCell>
              )}
              {showInvestorColumn && (
                <TableCell>
                  <Typography variant="body2">
                    {receipt.investor_name || '-'}
                  </Typography>
                </TableCell>
              )}
              <TableCell>
                {receipt.expected_date
                  ? format(new Date(receipt.expected_date), 'dd MMM yyyy')
                  : '-'}
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" fontWeight="medium">
                  {receipt.currency_code || currencyCode} {formatNumber(receipt.capital_amount)}
                </Typography>
              </TableCell>
              <TableCell>
                {receipt.days_overdue > 0 ? (
                  <Chip
                    label={`${receipt.days_overdue} days overdue`}
                    size="small"
                    color="error"
                  />
                ) : (
                  <Chip label="Awaiting" size="small" color="warning" />
                )}
              </TableCell>
              {onReceipt && (
                <TableCell align="center">
                  <Chip
                    icon={<ReceiptIcon />}
                    label="Receipt"
                    size="small"
                    color="success"
                    onClick={() => onReceipt(receipt)}
                    sx={{ cursor: 'pointer' }}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Matched Receipts Tab Component
function MatchedReceiptsTab({
  receipts = [],
  currencyCode = 'ZAR',
  showContractColumn = true,
  showInvestorColumn = false,
}) {
  if (receipts.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography color="textSecondary" fontWeight="medium" gutterBottom>
          No capital receipts recorded yet
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Capital receipts will appear here once recorded
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#e8f5e9' }}>
            {showContractColumn && <TableCell>Contract</TableCell>}
            {showInvestorColumn && <TableCell>Investor</TableCell>}
            <TableCell>Date Received</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell>Reference</TableCell>
            <TableCell>Method</TableCell>
            <TableCell>Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {receipts.map((receipt, index) => (
            <TableRow key={receipt.id || `receipt-${index}`} hover>
              {showContractColumn && (
                <TableCell>
                  <ContractReferenceCell reference={receipt.contract_reference || '-'} userReference={receipt.user_reference} />
                </TableCell>
              )}
              {showInvestorColumn && (
                <TableCell>
                  <Typography variant="body2">
                    {receipt.investor_name || '-'}
                  </Typography>
                </TableCell>
              )}
              <TableCell>
                {receipt.received_date
                  ? format(new Date(receipt.received_date), 'dd MMM yyyy')
                  : '-'}
              </TableCell>
              <TableCell align="right">
                <Typography fontWeight="medium" color="success.main">
                  {receipt.currency || currencyCode} {formatNumber(receipt.amount)}
                </Typography>
              </TableCell>
              <TableCell>{receipt.reference || '-'}</TableCell>
              <TableCell>{receipt.payment_method || 'EFT'}</TableCell>
              <TableCell>{receipt.notes || '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default PaymentTabs;
